import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { useMenuContext } from './context.js';
import { type MenuItemOwnProps } from './types.js';
import { LockSolidIcon } from '@contra/icons/icons';
import { type ElementType, type Ref } from 'react';
import { type PolymorphicPropsWithoutRef } from 'react-polymorphic-types';
import { css, styled } from 'styled-components';

const ICON_SIZE = 24;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MenuItemDefaultElement = 'div';

const IconContainer = styled.div<{
  $alignItems: 'center' | 'flex-start';
  $isLocked?: boolean;
  $withDarkUnlockedIcon: boolean;
}>`
  ${({ $alignItems, $isLocked, $withDarkUnlockedIcon, theme }) => css`
    position: relative;
    display: flex;
    align-items: center;
    ${$alignItems === 'flex-start' ? 'margin-top: 4px;' : ''}

    & > svg {
      color: ${$isLocked
        ? theme.colorsV2.gray70
        : $withDarkUnlockedIcon
          ? theme.colorsV2.gray90
          : `inherit`};
    }

    ${!$isLocked &&
    css`
      cursor: pointer;
      pointer-events: none;

      &:hover,
      &:focus {
        background: ${theme.colorsV2.gray90};
      }
    `}
  `}
`;

const MenuItemContent = styled.span`
  width: 100%;
`;

const MenuItemBox = styled.div<{
  $alignItems: 'center' | 'flex-start';
  $darkMode: boolean;
  $destructive?: boolean;
  $hasIcon: boolean;
  $noPaddingXOnMobile?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
}>`
  ${({
    $alignItems,
    $darkMode,
    $destructive,
    $hasIcon,
    $noPaddingXOnMobile,
    disabled,
    readOnly,
    theme,
  }) => css`
    width: 100%;
    min-width: 160px;
    cursor: pointer;
    cursor: ${disabled ? 'not-allowed' : readOnly ? 'default' : 'pointer'};
    display: flex;
    align-items: ${$alignItems};
    color: ${$darkMode
      ? theme.colorsV2.gray60
      : $destructive
        ? theme.colorsV2.red50
        : theme.colorsV2.black};

    ${disabled
      ? css`
          > * {
            color: ${theme.colorsV2.gray40};
            svg {
              color: ${theme.colorsV2.gray40};
            }
          }
        `
      : undefined};

    padding: ${$hasIcon ? '12px 6px' : '12px 20px'};

    ${$noPaddingXOnMobile
      ? css`
          padding-left: 0;
          padding-right: 0;
        `
      : undefined}

    & > * + * {
      margin-left: 12px;
    }

    ${theme.mediaQueries.lg} {
      min-height: 48px;
      padding: ${$hasIcon ? '18px 12px' : '12px 16px'};

      &:hover,
      &:focus,
      &:active {
        color: ${$darkMode
          ? theme.colorsV2.white
          : $destructive
            ? theme.colorsV2.red80
            : theme.colorsV2.black};
        background: ${$darkMode
          ? theme.colorsV2.gray90
          : $destructive
            ? theme.colorsV2.red10
            : theme.colorsV2.gray10};
      }
    }
  `}
`;

const StyledNavbarLockIcon = styled(LockSolidIcon)`
  ${({ theme }) => css`
    position: absolute;
    bottom: 0px;
    right: -2px;
    color: ${theme.colorsV2.black} !important;
  `}
`;

const StyledLockSolidIcon = styled(LockSolidIcon)`
  ${() => css`
    display: flex;
    flex-shrink: 0;
    margin-left: unset;
  `}
`;

const TooltipContainer = styled.div`
  display: flex;
  width: 100%;
  opacity: 1;
  overflow: visible;

  > div {
    display: flex;
    width: 100%;

    > div {
      display: flex;
      width: 100%;
    }
  }
`;

type MenuItemProps<C extends ElementType = typeof MenuItemDefaultElement> =
  PolymorphicPropsWithoutRef<MenuItemOwnProps, C>;

export const MenuItem = <
  C extends ElementType = typeof MenuItemDefaultElement,
>({
  alignItems = 'center',
  as,
  children,
  closeMenuOnClick = true,
  destructive,
  disabled = false,
  disabledIconPlacement = 'left',
  disabledTooltip,
  disabledTooltipPlacement = 'top',
  elementRef,
  hideDisabledLockIcon = false,
  icon: Icon,
  iconFillColor,
  iconSize = ICON_SIZE,
  noPaddingXOnMobile = false,
  onClick,
  onKeyDown,
  withDarkUnlockedIcon = false,
  ...props
}: MenuItemProps<C>) => {
  const { darkMode, toggleMenu } = useMenuContext();
  const hasIcon = Boolean(Icon);

  const content = (
    <MenuItemBox
      $alignItems={alignItems}
      $darkMode={darkMode}
      $destructive={destructive}
      $hasIcon={hasIcon}
      $noPaddingXOnMobile={noPaddingXOnMobile}
      aria-disabled={disabled}
      as={as as undefined}
      disabled={disabled}
      onClick={(event) => {
        if (!disabled) {
          if (closeMenuOnClick) toggleMenu();
          if (onClick) onClick(event);
        }
      }}
      onKeyDown={onKeyDown}
      ref={elementRef as Ref<HTMLDivElement>}
      role="menuitem"
      tabIndex={0}
      withDarkUnlockedIcon={withDarkUnlockedIcon}
      {...props}
    >
      <>
        {Icon && (
          <IconContainer
            $alignItems={alignItems}
            $isLocked={disabled}
            $withDarkUnlockedIcon={withDarkUnlockedIcon}
          >
            <Icon
              size={iconSize}
              {...(iconFillColor ? { style: { color: iconFillColor } } : {})}
            />
            {disabled &&
            !hideDisabledLockIcon &&
            disabledIconPlacement === 'left' ? (
              <StyledNavbarLockIcon size={16} />
            ) : null}
          </IconContainer>
        )}
        <MenuItemContent>{children}</MenuItemContent>
        {disabled &&
        !hideDisabledLockIcon &&
        disabledIconPlacement === 'right' ? (
          <StyledLockSolidIcon size={16} />
        ) : null}
      </>
    </MenuItemBox>
  );

  if (disabled && disabledTooltip) {
    return (
      <TooltipContainer>
        <Tooltip
          content={disabledTooltip}
          placement={disabledTooltipPlacement}
          strategy="absolute"
        >
          {content}
        </Tooltip>
      </TooltipContainer>
    );
  }

  return content;
};
